<template>
    <div data-cy="processes-button">
        <div
            class="d-flex align-center justify-space-between ga-2 mx-2 cursor-pointer v-list-item-copy-class"
            :class="{ 'list-item-active-highlight': isActiveRoute }"
            @click="router.push('/processes')"
        >
            <div class="d-flex align-center ga-2">
                <ActivityIcon size="16" />
                <p class="text-body-2">{{ t('processes.processes') }}</p>
            </div>
            <div class="d-flex align-center ga-2">
                <v-chip v-if="currentProcesses.length > 0" label color="primary" size="x-small">
                    {{ currentProcesses.length }}
                </v-chip>

                <BellIcon v-if="userActionNeeded" size="16" class="bell-animate" />
            </div>
        </div>

        <!-- Process notification snackbar -->
        <v-snackbar
            v-model="showProcessSnackbar"
            location="bottom right"
            :timeout="5000"
            color="primary"
            class="process-snackbar"
        >
            <div class="d-flex align-center">
                <BellIcon size="16" class="mr-2" />
                <span>{{ snackbarMessage }}</span>
            </div>
            <template #actions>
                <v-btn color="white" variant="text" size="small" @click="navigateToProcesses">
                    {{ t('processes.snackbar.view') }}
                </v-btn>
                <v-btn
                    color="white"
                    variant="text"
                    size="small"
                    @click="showProcessSnackbar = false"
                >
                    {{ t('processes.snackbar.dismiss') }}
                </v-btn>
            </template>
        </v-snackbar>
    </div>
</template>

<script setup>
import { BellIcon, ActivityIcon } from 'lucide-vue-next';
import { useRouter } from 'vue-router';
import { useProcessStore } from '@/stores/process';
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
const { t } = useI18n();
const router = useRouter();

const processStore = useProcessStore();

const { currentWorkspaceProcesses, processLoading } = storeToRefs(processStore);
const showProcessSnackbar = ref(false);
const snackbarMessage = ref('');

const currentProcesses = computed(() => currentWorkspaceProcesses.value);
const userActionNeeded = computed(() => {
    return currentWorkspaceProcesses.value.some(
        (process) => process.status === 'completed' && process.type === 'crawl_website',
    );
});

const navigateToProcesses = () => {
    showProcessSnackbar.value = false;
    router.push('/processes');
};

const isActiveRoute = computed(() => {
    return router.currentRoute.value.name === 'processes-view';
});

// Create function to watch process changes and trigger notifications
const setupProcessWatchers = () => {
    return watch(
        [() => currentProcesses.value.length, () => userActionNeeded.value],
        ([newProcessCount, newUserActionNeeded], [oldProcessCount, oldUserActionNeeded]) => {
            console.log(newProcessCount, newUserActionNeeded, oldProcessCount, oldUserActionNeeded);
            if (newProcessCount > oldProcessCount && newProcessCount > 0) {
                snackbarMessage.value = t('processes.snackbar.new_process_started');
                showProcessSnackbar.value = true;
            } else if (newUserActionNeeded && !oldUserActionNeeded) {
                snackbarMessage.value = t('processes.snackbar.process_needs_attention');
                showProcessSnackbar.value = true;
            }
        },
    );
};

// Create function to initialize watchers after loading
const initializeWatchers = () => {
    const unwatch = watch(
        () => processLoading.value,
        (newLoadingState) => {
            if (!newLoadingState) {
                // Processes are loaded, now set up the process watchers
                setupProcessWatchers();
                // Stop watching the loading state
                unwatch();
            }
        },
    );
};

onMounted(() => {
    initializeWatchers();
});
</script>

<style scoped lang="scss">
.v-list-item-copy-class {
    min-height: 34px;
    border-radius: 8px;
    border-color: rgba(var(--v-border-color), var(--v-border-opacity));
    border-style: solid;
    border-width: 0;
    outline: none;
    max-width: 100%;
    padding: 4px 8px;
    position: relative;
    text-decoration: none;
}

@keyframes bellShake {
    0% {
        transform: rotate(0);
    }
    15% {
        transform: rotate(25deg);
    }
    30% {
        transform: rotate(-25deg);
    }
    45% {
        transform: rotate(20deg);
    }
    60% {
        transform: rotate(-20deg);
    }
    75% {
        transform: rotate(10deg);
    }
    85% {
        transform: rotate(-10deg);
    }
    100% {
        transform: rotate(0);
    }
}

.bell-animate {
    animation: bellShake 1s cubic-bezier(0.36, 0, 0.66, -0.56) forwards;
    transform-origin: top center; /* This makes the bell swing from the top */
}
</style>
